/**
 * Thème de l'interface : sombre par défaut, clair au choix.
 *
 * Le portail suivait la préférence du système (`darkMode: 'media'`), sans bascule
 * possible. Deux raisons d'en changer : le sombre est le mode attendu par défaut
 * ici, quelle que soit la configuration du poste, et un outil qu'on ouvre pour
 * lire des journaux mérite qu'on puisse passer au clair sans toucher aux réglages
 * de son système.
 *
 * Le choix est mémorisé. Son absence vaut « sombre » et non « comme le système » :
 * c'est une décision de produit, pas un oubli — un poste réglé en clair afficherait
 * sinon un portail clair alors que le sombre est ce qu'on veut montrer.
 *
 * Ces fonctions sont pures et sans dépendance au navigateur au-delà de l'élément
 * qu'on leur passe : c'est ce qui les rend vérifiables, et la lecture du stockage
 * est laissée à l'appelant.
 */

export type Theme = 'dark' | 'light';

/** Clé de stockage. Préfixée : un domaine peut héberger d'autres applications. */
export const THEME_STORAGE_KEY = 'portail:theme';

/** Thème appliqué en l'absence de choix mémorisé. */
export const DEFAULT_THEME: Theme = 'dark';

/** Classe portée par `<html>`, attendue par Tailwind (`darkMode: 'class'`). */
export const DARK_CLASS = 'dark';

/** Valide une valeur venue du stockage, qui n'est pas de confiance. */
export function parseTheme(raw: string | null | undefined): Theme | null {
  return raw === 'dark' || raw === 'light' ? raw : null;
}

/** Thème à appliquer : le choix mémorisé, sinon le défaut. */
export function resolveTheme(stored: string | null | undefined): Theme {
  return parseTheme(stored) ?? DEFAULT_THEME;
}

/** L'autre thème, pour le bouton de bascule. */
export function otherTheme(theme: Theme): Theme {
  return theme === 'dark' ? 'light' : 'dark';
}

/**
 * Applique le thème à l'élément racine.
 *
 * `color-scheme` est posé en même temps que la classe : sans lui, les éléments
 * que le navigateur dessine lui-même — barres de défilement, listes déroulantes
 * natives, champs de date — resteraient clairs sur un fond sombre.
 */
export function applyTheme(root: HTMLElement, theme: Theme): void {
  root.classList.toggle(DARK_CLASS, theme === 'dark');
  root.style.colorScheme = theme;
}
